import React, { useState, useEffect } from 'react';
import { API_BASE } from '../config/api';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import '../App.css';

function LoginPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isRegister, setIsRegister] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [status, setStatus] = useState('');
  const [resetStatus, setResetStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingReset, setLoadingReset] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Détection mobile simple pour styling :
  const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 600;

  const [inviteToken, setInviteToken]         = useState("");
  const [guestUsername, setGuestUsername]     = useState("");
  const [guestEmail, setGuestEmail]           = useState("");
  const [guestEmail2, setGuestEmail2]         = useState("");
  const [guestPassword, setGuestPassword]     = useState("");
  const [guestPassword2, setGuestPassword2]   = useState("");
  const [guestStatus, setGuestStatus]         = useState("");
  const [guestLoading, setGuestLoading]       = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("verified") === "1") {
      setStatus("✅ Votre email a bien été vérifié. Vous pouvez vous connecter.");
    }
    const token = params.get("invite");
    if (token) {
      setInviteToken(token);
    }
  }, []);

  const handleGuestRegister = async (e) => {
    e.preventDefault();
    setGuestStatus("");
    if (!guestUsername || !guestEmail || !guestEmail2 || !guestPassword || !guestPassword2) {
      setGuestStatus("❌ Tous les champs sont requis");
      return;
    }
    if (guestEmail.toLowerCase() !== guestEmail2.toLowerCase()) {
      setGuestStatus("❌ Les adresses email ne correspondent pas");
      return;
    }
    if (guestPassword !== guestPassword2) {
      setGuestStatus("❌ Les mots de passe ne correspondent pas");
      return;
    }
    setGuestLoading(true);
    try {
      const res = await fetch(`${API_BASE}/user/register_guest.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          token: inviteToken,
          username: guestUsername,
          email: guestEmail,
          email2: guestEmail2,
          password: guestPassword,
          password2: guestPassword2,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.success) {
        window.history.replaceState({}, '', '/');
        if (typeof onLogin === "function") {
          onLogin(data.user?.username || guestUsername, "guest");
        }
      } else {
        setGuestStatus("❌ " + (data.error || "Erreur"));
      }
    } catch {
      setGuestStatus("❌ Erreur réseau");
    } finally {
      setGuestLoading(false);
    }
  };

  /* ========= Auto-login via cookie de session/remember ========= */
  useEffect(() => {
    (async () => {
      try {
        const endpoints = [
          `${API_BASE}/user/session_me.php`, // recommandé
          `${API_BASE}/user/me.php`,         // fallback si ancien fichier
        ];
        for (const url of endpoints) {
          const r = await fetch(url, { credentials: 'include' });
          if (!r.ok) continue;
          const j = await r.json().catch(() => null);
          if (j?.authenticated) {
            if (typeof onLogin === 'function') {
              onLogin(j.user?.username || j.user?.email, j.user?.role);
            }
            break;
          }
        }
      } catch {
        /* silencieux */
      }
    })();
  }, [onLogin]);
  /* ========================================================================= */

  const handleResetRequest = async () => {
    if (!resetEmail) {
      setResetStatus('❌ Merci de saisir votre email');
      return;
    }
    setLoadingReset(true);
    setResetStatus('');
    try {
      const res = await fetch(`${API_BASE}/user/request_reset.php`, {
        method: 'POST',
        body: new URLSearchParams({ email: resetEmail }),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      const text = await res.text();
      const success = typeof text === 'string' && text.includes('✅');
      setResetStatus(success ? text : `❌ ${text}`);
    } catch {
      setResetStatus("❌ Échec de l'envoi du lien de réinitialisation");
    } finally {
      setLoadingReset(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setStatus('');
    setLoading(true);

    if (isRegister) {
      if (!username || !email || !password || !confirmPassword) {
        setStatus('❌ Merci de remplir tous les champs');
        setLoading(false);
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setStatus("❌ Format d'email non valide");
        setLoading(false);
        return;
      }
      if (password.length < 6) {
        setStatus("❌ Le mot de passe doit contenir au moins 6 caractères");
        setLoading(false);
        return;
      }
      if (password !== confirmPassword) {
        setStatus("❌ Les mots de passe ne sont pas identiques");
        setLoading(false);
        return;
      }
    }

    const endpoint = isRegister
      ? `${API_BASE}/user/register.php`
      : `${API_BASE}/user/login.php`;
    const payload = isRegister
      ? new URLSearchParams({ username, email: email.trim(), password: password.trim(), invite_token: inviteToken || '' })
      : new URLSearchParams({ email: email.trim(), password: password.trim(), remember: rememberMe ? '1' : '' });

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        body: payload,
        credentials: 'include', // indispensable pour cookie serveur
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); }
      catch { data = { success: text.includes('✅'), message: text }; }

      if (res.ok && data.success) {
        if (isRegister) {
          setStatus('✅ Enregistrement réussi. Vérifiez vos emails.');
          setIsRegister(false);
          setUsername(''); setEmail(''); setPassword(''); setConfirmPassword('');
          // Si un token d'invitation attend, on peut suggérer de se connecter
          if (inviteToken) {
            setStatus("✅ Compte créé ! Consultez vos emails pour confirmer votre adresse, puis connectez-vous pour rejoindre le foyer.");
          }
        } else {
          if (inviteToken) {
            try {
              const invRes = await fetch(`${API_BASE}/user/accept_invitation.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ token: inviteToken }),
              });
              const invData = await invRes.json().catch(() => ({}));
              if (invData.success) {
                setStatus("✅ Invitation acceptée ! Bienvenue dans le foyer.");
              }
            } catch {}
          }
          if (typeof onLogin === 'function') {
            onLogin(data.user?.username || data.user?.email || email, data.user?.role);
          }
        }
      } else {
        setStatus(data.message || (isRegister ? '❌ Échec création du compte' : '❌ Échec de connexion'));
      }
    } catch {
      setStatus('❌ Erreur inattendue');
    } finally {
      setLoading(false);
    }
  };

  // STYLE pour centrage et largeur optimale des inputs sur mobile/desktop
  const inputAreaStyle = {
    width: isMobile ? '78vw' : 360,
    minWidth: isMobile ? 198 : 220,
    maxWidth: 370,
    margin: '10px auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  };
  const inputStyle = {
    width: isMobile ? '94%' : 300,
    minWidth: isMobile ? 130 : 180,
    maxWidth: 300,
    margin: isMobile ? '5px auto' : '7px auto',
    fontSize: isMobile ? 16 : 17,
    padding: isMobile ? '10px 13px' : '12px 16px',
    borderRadius: 7,
    border: '1.3px solid #888',
    boxSizing: 'border-box',
    display: 'block',
    background: '#fff',
    color: '#000'  // <-- correction : texte noir
  };

  return (
    <div
      className="login-container"
      style={{
        minHeight: '100vh',
        width: '100%',
        background: '#f1f8e9',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        paddingTop: 30
      }}
    >
      {/* Logo du tableau de bord, responsive */}
      <img
        src="/logo-fond-transparent.png"
        alt="Logo Lumieres de Jardin"
        style={{
          width: isMobile ? 108 : 180,
          height: isMobile ? 90 : 150,
          marginBottom: isMobile ? 15 : 20,
          marginTop: isMobile ? 4 : 18
        }}
      />

      {inviteToken ? (
        <form
          onSubmit={handleGuestRegister}
          autoComplete="off"
          style={{
            ...inputAreaStyle,
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 2px 28px rgba(10,34,80,0.10)',
            padding: isMobile ? '26px 2vw 18px 2vw' : '32px 28px',
            marginBottom: 24,
          }}
        >
          <h2 style={{ textAlign: 'center', marginBottom: 6, marginTop: 0, fontWeight: 700, color: '#056b4d', fontSize: isMobile ? 18 : 22 }}>
            Rejoindre le foyer
          </h2>
          <p style={{ textAlign: 'center', fontSize: 13, color: '#555', marginBottom: 18 }}>
            Créez votre compte pour accéder aux appareils partagés.
          </p>
          {guestStatus && (
            <p style={{ textAlign: 'center', color: guestStatus.startsWith('❌') ? '#c00' : '#056b4d', fontSize: 14, marginBottom: 10 }}>
              {guestStatus}
            </p>
          )}
          {[
            { label: 'Nom', value: guestUsername, set: setGuestUsername, type: 'text', ac: 'name' },
            { label: 'Email', value: guestEmail, set: setGuestEmail, type: 'email', ac: 'email' },
            { label: 'Confirmer l\'email', value: guestEmail2, set: setGuestEmail2, type: 'email', ac: 'email' },
            { label: 'Mot de passe', value: guestPassword, set: setGuestPassword, type: 'password', ac: 'new-password' },
            { label: 'Confirmer le mot de passe', value: guestPassword2, set: setGuestPassword2, type: 'password', ac: 'new-password' },
          ].map(({ label, value, set, type, ac }) => (
            <input
              key={label}
              type={type}
              placeholder={label}
              value={value}
              autoComplete={ac}
              onChange={e => set(e.target.value)}
              style={{ ...inputStyle, marginBottom: 10 }}
            />
          ))}
          <button
            type="submit"
            disabled={guestLoading}
            style={{ width: '100%', marginTop: 8, padding: '12px', borderRadius: 7, background: '#00FFA3', border: 'none', fontWeight: 700, fontSize: 16, cursor: 'pointer' }}
          >
            {guestLoading ? 'Création...' : 'Créer mon compte'}
          </button>
        </form>
      ) : (

      <form
        onSubmit={handleSubmit}
        autoComplete="on"
        style={{
          ...inputAreaStyle,
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 2px 28px rgba(10,34,80,0.10)',
          padding: isMobile ? '26px 2vw 18px 2vw' : '32px 28px',
          marginBottom: 24,
        }}
      >
        <h2 style={{ textAlign: 'center', marginBottom: 20, marginTop: 0, fontWeight: 700, color: '#056b4d', fontSize: isMobile ? 19 : 23 }}>
          {isRegister ? 'Créer un compte' : 'Connexion à Lumières de Jardin'}
        </h2>

        {status && (
          <p
            className={`status-message ${status.includes('✅') ? 'status-success' : 'status-error'}`}
            style={{ margin: '12px 0', fontSize: isMobile ? 13.5 : 15, textAlign: 'center' }}
          >
            {status}
          </p>
        )}

        {isRegister && (
          <div style={{ width: '100%' }}>
            <label style={{ marginLeft: 3, fontSize: isMobile ? 15 : 16 }}>Nom d'utilisateur</label>
            <input
              type="text"
              name="username"
              autoComplete="username"
              value={username}
              onChange={e => setUsername(e.target.value)}
              required
              style={inputStyle}
            />
          </div>
        )}

        <div style={{ width: '100%' }}>
          <label style={{ marginLeft: 3, fontSize: isMobile ? 15 : 16 }}>Email</label>
          <input
            type="email"
            name="email"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            inputMode="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            autoFocus={!isRegister}
            style={inputStyle}
          />
        </div>

        <div style={{ width: '100%' }}>
          <label style={{ marginLeft: 3, fontSize: isMobile ? 15 : 16 }}>Mot de passe</label>
          <div style={{ position: 'relative', width: '100%' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              autoComplete={isRegister ? 'new-password' : 'current-password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              style={{ ...inputStyle, paddingRight: 36 }}
            />
            <span
              onClick={() => setShowPassword(v => !v)}
              style={{
                position: 'absolute', right: 18, top: '50%',
                transform: 'translateY(-50%)', cursor: 'pointer', color: '#888'
              }}
              tabIndex={-1}
              title={showPassword ? 'Cacher' : 'Voir le mot de passe'}
            >
              {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
            </span>
          </div>
        </div>

        {isRegister && (
          <div style={{ width: '100%' }}>
            <label style={{ marginLeft: 3, fontSize: isMobile ? 15 : 16 }}>Confirmer le mot de passe</label>
            <input
              type="password"
              name="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
              style={inputStyle}
            />
          </div>
        )}

        {!isRegister && (
          <div style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            margin: isMobile ? '8px 0 10px 3px' : '10px 0 13px 5px'
          }}>
            <input
              type="checkbox"
              id="rememberMe"
              name="remember"
              checked={rememberMe}
              onChange={e => setRememberMe(e.target.checked)}
              style={{ marginRight: 9, width: isMobile ? 18 : 18, height: isMobile ? 18 : 18 }}
            />
            <label htmlFor="rememberMe" style={{ fontSize: isMobile ? 15.6 : 17, color: '#385', fontWeight: 500, cursor: 'pointer' }}>
              Rester connecté
            </label>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          style={{
            width: isMobile ? '94%' : 240,
            maxWidth: 350,
            margin: isMobile ? '12px 0 4px 0' : '13px 0 8px 0',
            padding: isMobile ? '12px' : '14px 0',
            fontWeight: 700,
            fontSize: isMobile ? 15.5 : 18,
            letterSpacing: 0.12,
            borderRadius: 7,
            border: 'none',
            background: 'linear-gradient(92deg,#009680 60%,#00FFA3 130%)',
            color: '#fff',
            boxShadow: '0 4px 9px 0 #2abe903a'
          }}
        >
          {loading ? (isRegister ? 'Création...' : 'Connexion...') : (isRegister ? 'Créer le compte' : 'Se connecter')}
        </button>

        <div
          className="login-actions"
          style={{ textAlign: 'center', width: '102%', marginTop: isMobile ? 8 : 13 }}
        >
          <button
            type="button"
            onClick={() => setIsRegister(v => !v)}
            style={{ fontSize: isMobile ? '13px' : '15px', background: 'none', textDecoration: 'underline', border: 'none', color: '#026641ff', marginRight: '16px', cursor: 'pointer' }}
          >
            {isRegister ? 'Déjà inscrit ? Se connecter' : 'Créer un compte'}
          </button>
          <button
            type="button"
            onClick={() => setShowResetForm(v => !v)}
            style={{ fontSize: isMobile ? '13px' : '15px', background: 'none', textDecoration: 'underline', border: 'none', color: '#026641ff', cursor: 'pointer' }}
          >
            Mot de passe oublié ?
          </button>
        </div>
      </form>
      )}

      {!inviteToken && showResetForm && (
        <div style={{
          background: '#f1f1f1',
          borderRadius: 9,
          padding: 15,
          margin: '20px 0',
          width: isMobile ? '88vw' : 300,
          maxWidth: 380,
          boxShadow: '0 3px 16px -3px #919'
        }}>
          <h3 style={{ marginTop: 0, color: '#006' }}>Réinitialiser le mot de passe</h3>
          <input
            type="email"
            name="resetEmail"
            autoComplete="email"
            placeholder="Votre email"
            value={resetEmail}
            onChange={e => setResetEmail(e.target.value)}
            style={{ width: '100%', marginBottom: 10, ...inputStyle, borderRadius: 7, padding: isMobile ? '9px' : '11px' }}
          />
          <button
            onClick={handleResetRequest}
            disabled={loadingReset}
            style={{
              width: '100%',
              marginTop: 4,
              padding: isMobile ? '11px' : '13px 0',
              background: '#009680',
              color: '#fff',
              border: 'none',
              fontWeight: 600,
              borderRadius: 7,
              fontSize: isMobile ? 14.8 : 16
            }}
          >
            {loadingReset ? 'Envoi en cours...' : 'Envoyer le lien'}
          </button>
          {resetStatus && (
            <p className={`status-message ${resetStatus.includes('✅') ? 'status-success' : 'status-error'}`} style={{ marginTop: 10 }}>
              {resetStatus}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default LoginPage;
